import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { trackEvent } from "@/utils/tagManagerUtils";
import { ArrowRight, Building2, Users, ShieldCheck, Briefcase } from "lucide-react";

const GoogleAdsAgencySoftware = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("page_view", { path: "/google-ads-agency-software" });
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads Agency Software | Scale Your PPC Agency | Upfixe</title>
        <meta
          name="description"
          content="Upfixe is the premier Google Ads agency software. Scale your operations, onboard clients faster, and reduce manual account management overhead with AI automation."
        />
        <meta
          name="keywords"
          content="google ads agency software, ppc agency software, agency tools for google ads, scale ppc agency, white label google ads"
        />
        <link rel="canonical" href="https://upfixe.com/google-ads-agency-software" />
      </Helmet>

      <Header />

      <main className="flex-grow pt-24 pb-16">
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 font-medium text-sm mb-6">
                <Building2 className="h-4 w-4" /> Built for Marketing Agencies
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                The Ultimate <span className="text-primary">Google Ads Agency Software</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Your agency's biggest bottleneck is the time it takes to set up, launch, and manage new client campaigns. 
                Upfixe removes that bottleneck with AI-driven, multi-account automation.
              </p>
              <div className="flex gap-4">
                <Button asChild size="lg" className="text-lg px-6 py-6">
                  <a href="https://app.upfixe.com/signup">
                    Try the Agency Plan
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-6 py-6">
                  <Link to="/pricing">View Agency Pricing</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-blue-400/20 rounded-3xl transform rotate-3"></div>
              <div className="bg-white rounded-3xl shadow-xl p-8 relative border border-gray-100">
                <h3 className="text-2xl font-bold mb-6">Agency Scale Metrics</h3>
                <div className="space-y-6">
                  <div className="border-b border-gray-100 pb-4">
                    <p className="text-sm text-gray-500 uppercase font-semibold">Client Onboarding Time</p>
                    <p className="text-3xl font-bold text-gray-900">↓ 85% Faster</p>
                  </div>
                  <div className="border-b border-gray-100 pb-4">
                    <p className="text-sm text-gray-500 uppercase font-semibold">Campaign Setup Margin</p>
                    <p className="text-3xl font-bold text-green-600">↑ 3x Increase</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase font-semibold">Max Client Capacity</p>
                    <p className="text-3xl font-bold text-blue-600">No Limit</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-16">Why Top PPC Agencies Choose Upfixe</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <Users className="h-8 w-8 text-primary mb-4" />
                <h4 className="text-xl font-bold mb-2">MCC Account Native</h4>
                <p className="text-gray-600">
                  Connect your Google Ads Manager Account (MCC) once. Upfixe securely pulls in all client sub-accounts 
                  so you can switch between them seamlessly.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <Briefcase className="h-8 w-8 text-primary mb-4" />
                <h4 className="text-xl font-bold mb-2">Infinite Scalability</h4>
                <p className="text-gray-600">
                  Whether you have 5 clients or 50, Upfixe's AI generates custom, high-quality campaigns for any niche 
                  without draining your account managers' time.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <ShieldCheck className="h-8 w-8 text-primary mb-4" />
                <h4 className="text-xl font-bold mb-2">Safe "Paused" Deployment</h4>
                <p className="text-gray-600">
                  Maintain strict quality control. Upfixe builds everything perfectly but always pushes campaigns to Google Ads 
                  in a PAUSED state for final manual review.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GoogleAdsAgencySoftware;
